// /src/pages/LandingVariant.jsx
// Single-component, multi-theme landing pages per advertising (Sfarinà).
// 4 varianti: budget, vegan, group, premium. Tema cromatico via CSS variables
// pilotate da `data-theme` sul container — un solo file React, zero duplicazione.
//
// Tracking: ogni LP eredita il pipeline server-side esistente (sendServerSideEvent
// → Meta CAPI + GA4 MP + Google Ads conversion sul Lead/Schedule).
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import LeadFormModal from '../components/LeadFormModal';
import LpLeadModal from '../components/LpLeadModal';
import ReservationModal from '../components/ReservationModal';
import SiteFooter from '../components/SiteFooter';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useT } from '../i18n/LanguageContext';
import { trackEventDedup, sendServerSideEvent } from '../lib/tracking';

const IMG = {
  rotoli: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/kbfr5bja_Progetto%20senza%20titolo%20%283%29.jpg',
  piadine: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/vehs674y_Progetto%20senza%20titolo%20%281%29.jpg',
  crescioni: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/4phkvvav_Progetto%20senza%20titolo%20%285%29.jpg',
  tacos: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/poe58oxw_Progetto%20senza%20titolo%20%284%29.jpg',
  contrast: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/7f5p7ewr_Progetto%20senza%20titolo%20%282%29.jpg',
  segreto: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/7d9cvcoi_Progetto%20senza%20titolo%20%286%29.jpg',
  hero: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/lk362ars_Progetto%20senza%20titolo.jpg',
  vegana_hero: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/lkyiiehf_Vegana.png',
  panino_giacomino: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/dg3xkiew_Panino%20Giacomino.png',
};

// ===== Configurazione delle 4 varianti =====
// MARKETING: per modificare i copy, edita qui sotto.
const VARIANTS = {
  // ╔════════════════ BUDGET ════════════════════════════════════════╗
  // 🎯 Target: chi spende ~20€ in pausa pranzo restando insoddisfatto
  // 🎨 Tema: rosso aranciato + giallo caldo (urgenza + appetito)
  budget: {
    theme: 'budget',
    slug: 'rotolo-60cm',
    seo_title: 'Sfarinà — Rotolo Gourmet 60 cm a meno di 15€',
    hero_kicker: 'Stai sprecando 20€?',
    hero_h1: 'Vorresti saziarti con meno di 15€?',
    hero_h2: 'Assaggia il rotolo gourmet più lungo di Bologna. 60 cm di Rotolo con abbinamenti e ingredienti selezionati da uno chef con esperienza in cucine stellate Michelin — non può non essere buono.',
    trust: 'Ormai è sempre la stessa storia: spendi quasi 20€, sei sazio, ma estremamente insoddisfatto.',
    trust_is_pain: true,
    showcase_title: 'Guarda cosa puoi mangiare con 13,50€',
    showcase_subtitle: 'Rotolo gourmet 60 cm — abbinamenti firmati da chef stellati Michelin',
    showcase_images: [
      { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/ll6vln6j_Roll%20Adele.png', alt: 'Rotolo Adele', caption: 'Rotolo Gigante con Burrata Pugliese Fresca e Artigianale, Prosciutto Cotto alta qualità e una manciata di Dolci Pomodorini Semi Secchi Homemade.' },
      { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/3d984d31_Food%20Porn%20Roll.png', alt: 'Rotolo Foodporn', caption: 'Rotolo Gigante con Tacchino, Scaglie di Parmigiano Reggiano 24 mesi D.O.P., Insalata Fresca e colata di Maionese.' },
      { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/mslibgk0_Classic%20Roll.png', alt: 'Rotolo Classico', caption: 'Rotolo Gigante con Prosciutto di Parma D.O.P. 24 mesi, Squacquerone Cremoso di Romagna D.O.P., Rucola.' },
    ],
    offer_kicker: 'Bibita in omaggio',
    offer_h: '1 bibita in Omaggio sul primo Rotolo',
    offer_body: 'Lascia i tuoi dati e ricevi il coupon su WhatsApp — ordini un rotolo gigante da Sfarinà e ti regaliamo una bibita fresca.',
    cta_main: 'Ottieni la tua bibita in omaggio',
    cta_secondary: 'Vai al menù completo',
    cta_type: 'lead',
    lead_modal_copy: {
      kicker: 'Solo per veri amanti dei Rotoli',
      heading: 'Ricevi la tua Bibita in omaggio',
      subheading: 'Lascia i tuoi dati, ti arriva il coupon su WhatsApp. Lo usi sul tuo primo rotolo gigante da Sfarinà.',
      submit: 'Voglio la mia bibita gratis',
      success_body: 'A breve ti arriva il coupon su WhatsApp. Ti aspettiamo da Sfarinà con il tuo rotolo gigante e una bibita fresca offerta da noi 🥤',
      offer_tag: 'Budget — 1 bibita in omaggio sul primo rotolo',
    },
    i18n_en: {
      seo_title: 'Sfarinà — 60 cm Gourmet Roll for under €15',
      hero_kicker: 'Wasting €20?',
      hero_h1: 'Want to get really full for under €15?',
      hero_h2: 'Taste the longest gourmet roll in Bologna. 60 cm of Roll with pairings and ingredients selected by a chef with experience in Michelin-starred kitchens — it can\'t not be good.',
      trust: 'Same story every time: you spend almost €20, you\'re full, but extremely unsatisfied.',
      showcase_title: 'See what you can eat with €13.50',
      showcase_subtitle: '60 cm gourmet roll — pairings signed by Michelin-starred chefs',
      offer_kicker: 'Free drink',
      offer_h: '1 free drink on your first Roll',
      offer_body: 'Leave your details and receive the coupon on WhatsApp — order a giant roll from Sfarinà and we\'ll give you a fresh drink.',
      cta_main: 'Get your free drink',
      cta_secondary: 'See the full menu',
      lead_modal_copy: {
        kicker: 'Only for true Roll lovers',
        heading: 'Get your free drink',
        subheading: 'Leave your details, your coupon arrives on WhatsApp. Use it on your first giant roll at Sfarinà.',
        submit: 'I want my free drink',
        success_body: 'Your coupon will arrive on WhatsApp shortly. We\'re waiting for you at Sfarinà with your giant roll and a fresh drink on us 🥤',
      },
    },
  },

  // ╔════════════════ VEGAN ════════════════════════════════════════╗
  vegan: {
    theme: 'vegan',
    slug: 'vegan',
    seo_title: 'Sfarinà — Piadina Vegana con impasto all\'olio EVO',
    hero_kicker: 'Stop alla pasta al pomodoro',
    hero_h1: 'Sei un vegano a Bologna?',
    hero_h2: 'Smettila di accontentarti della solita noiosa Pasta al Pomodoro.',
    hero_image: { src: IMG.vegana_hero, alt: 'Piadina Vegana con verdure grigliate e pesto' },
    trust: 'Impasto all\'olio extravergine di oliva, zero strutto, ingredienti di prima scelta.',
    showcase_title: 'Assaggia una Piadina con impasto all\'olio EVO',
    showcase_subtitle: 'Con soli ingredienti di prima scelta. 100% vegetale, 100% artigianale.',
    showcase_images: [
      { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/3kh9bgiy_Ravanello.png', alt: 'Piadina con Ravanello', caption: 'Fresca e croccante' },
      { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/kh17i3wr_Tacos%20Parmigiana.png', alt: 'Tacos Vegetariani', caption: 'Parmigiana di melanzane' },
      { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/7eb30mpb_Rucola%20e%20Squacquerone.png', alt: 'Piadina Vegetariana', caption: 'Rucola e squacquerone' },
    ],
    offer_kicker: 'Bibita in omaggio',
    offer_h: '1 bibita in Omaggio sul primo ordine',
    offer_body: 'Lascia i tuoi dati e ricevi il coupon su WhatsApp — ordini una piadina da Sfarinà e ti regaliamo una bibita fresca.',
    cta_main: 'Sblocca la tua Bibita in Omaggio',
    cta_secondary: null,
    cta_type: 'lead',
    lead_modal_copy: {
      kicker: 'Solo per veri amanti del Veg',
      heading: 'Ricevi la tua Bibita in omaggio',
      subheading: 'Lascia i tuoi dati, ti arriva il coupon su WhatsApp. Lo usi sulla tua prima piadina vegetariana da Sfarinà.',
      submit: 'Voglio la mia bibita gratis',
      success_body: 'A breve ti arriva il coupon su WhatsApp. Ti aspettiamo da Sfarinà con la tua piadina vegetariana e una bibita fresca offerta da noi 🌱',
      offer_tag: 'Vegan — 1 bibita in omaggio sulla prima piadina',
    },
    i18n_en: {
      seo_title: 'Sfarinà — Vegan Piadina with EVO oil dough',
      hero_kicker: 'Stop with the tomato pasta',
      hero_h1: 'Are you vegan in Bologna?',
      hero_h2: 'Stop settling for the same boring Pasta al Pomodoro.',
      trust: 'Extra virgin olive oil dough, zero lard, top-quality ingredients.',
      showcase_title: 'Taste a Piadina made with EVO oil dough',
      showcase_subtitle: 'Only top-quality ingredients. 100% plant-based, 100% handmade.',
      offer_kicker: 'Free drink',
      offer_h: '1 free drink on your first order',
      offer_body: 'Leave your details and receive the coupon on WhatsApp — order a piadina from Sfarinà and we\'ll give you a fresh drink.',
      cta_main: 'Get your free drink',
      lead_modal_copy: {
        kicker: 'Only for true Veg lovers',
        heading: 'Get your free drink',
        subheading: 'Leave your details, your coupon arrives on WhatsApp. Use it on your first vegetarian piadina at Sfarinà.',
        submit: 'I want my free drink',
        success_body: 'Your coupon will arrive on WhatsApp shortly. We\'re waiting for you at Sfarinà with your vegetarian piadina and a fresh drink on us 🌱',
      },
    },
  },

  // ╔════════════════ GROUP / DEGUSTAZIONE ═══════════════════════════╗
  group: {
    theme: 'group',
    slug: 'degustazione',
    seo_title: 'Sfarinà — Degustazione 5 piadine gourmet a Bologna',
    hero_kicker: 'Limited Edition',
    hero_h1: 'Non sai cosa assaggiare a Bologna?',
    hero_h2: 'Assaggia la nostra degustazione di Piadine gourmet — 5 piadine diverse da dividere con il tuo gruppo per provare la vera tradizione bolognese rivisitata da uno chef stellato Michelin.',
    trust: '4 menù diversi, 2 per ogni sede di Sfarinà. Degustazione Limited Edition.',
    showcase_title: 'La degustazione classica Sfarinà',
    showcase_subtitle: '5 piadine artigianali impastate a mano ogni mattina, da condividere',
    showcase_images: [
      { src: IMG.piadine, alt: 'Sfarinà', caption: 'Crudo 24 mesi DOP, squacquerone, rucola' },
      { src: IMG.crescioni, alt: 'Caduti di Cefalonia', caption: 'Mortadella DOP, burrata pugliese, pomodorini' },
      { src: IMG.tacos, alt: 'Aridaje', caption: 'Porchetta, patate, salsa al parmigiano 24m DOP' },
      { src: IMG.contrast, alt: 'Adele', caption: 'Prosciutto cotto, burrata, pomodorini' },
      { src: IMG.segreto, alt: 'La Spianata', caption: 'Spianata piccante, burrata, pomodorini' },
    ],
    showcase_long: [
      { name: 'Club Sandwich', desc: 'Petto di Pollo Grigliato, maionese senapata fatta in casa, lattuga, pomodoro fresco, croccante pancetta alla griglia e Fontina Valdostana DOP.' },
      { name: 'La Fede', desc: 'Prosciutto Cotto locale di alta qualità, crema di zucchine fatta in casa, chips di zucchine grigliate e Stracciatella di Burrata Pugliese.' },
      { name: 'Alberto', desc: 'Succosa Salsiccia artigianale grigliata, cipolla caramellata fatta in casa, salsa di peperoni arrosto agropiccante.' },
      { name: 'Franco', desc: 'Roastbeef di alta qualità affettato al momento, salsa tonnata fatta in casa, insalata fresca del Mercato delle Erbe.' },
      { name: 'Alessandro', desc: 'Polpette di Tonno e Patate Mantecate, salsa Tzatziki fatta in casa, rucola, pomodorini semisecchi homemade.' },
    ],
    offer_kicker: 'Posti limitati',
    offer_h: 'Prenota ora la tua Degustazione',
    offer_body: 'Limited Edition di stagione. Disponibile solo in entrambe le sedi di Bologna — Mercato delle Erbe e Cefalonia.',
    cta_main: 'Prenota ora la tua degustazione',
    cta_secondary: 'Vai al menù completo',
    cta_type: 'reservation',
    i18n_en: {
      seo_title: 'Sfarinà — Tasting of 5 gourmet piadine in Bologna',
      hero_kicker: 'Limited Edition',
      hero_h1: 'Don\'t know what to taste in Bologna?',
      hero_h2: 'Try our gourmet Piadine tasting — 5 different piadine to share with your group, to discover the true Bolognese tradition reinterpreted by a Michelin-starred chef.',
      trust: '4 different menus, 2 per Sfarinà location. Limited Edition tasting.',
      showcase_title: 'The classic Sfarinà tasting',
      showcase_subtitle: '5 hand-made piadine, kneaded fresh every morning, to share',
      offer_kicker: 'Limited seats',
      offer_h: 'Book your Tasting now',
      offer_body: 'Seasonal Limited Edition. Available only at both Bologna locations — Mercato delle Erbe and Cefalonia.',
      cta_main: 'Book your tasting now',
      cta_secondary: 'See the full menu',
    },
  },

  // ╔════════════════ PREMIUM / TURISTA ═════════════════════════╗
  premium: {
    theme: 'premium',
    slug: 'mortadella-igp',
    seo_title: 'Sfarinà — Panino Mortadella IGP a Bologna',
    hero_kicker: 'Turista',
    hero_h1: 'Non farti inculare dagli acchiappaturisti',
    hero_h2: 'Assaggia il Panino con la Mortadella più saporito di Bologna.',
    hero_image: { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/ylrtqmtl_Parmareggio.png', alt: 'Panino Parmareggio con Mortadella IGP, stracciatella, mandorle e miele' },
    trust: 'Pane di semola Bio • Mortadella IGP affettata al momento • Ingredienti di prima scelta',
    showcase_title: 'Perché il nostro Panino è migliore?',
    showcase_subtitle: 'Tre differenze nette che senti già al primo morso.',
    showcase_images: [
      { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/esbak03i_Mario%20Rossi.png', alt: 'Mario Rossi', caption: 'Mega Panino Artigianale di Semola caldo con una marea di Mortadella.' },
      { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/kmqdjd85_Gourmet.png', alt: 'Gourmet', caption: 'Mega Panino Artigianale di Semola caldo con una marea di Mortadella, Mandorle Tostate, Colata di Mousse di Gorgonzola dolce e Miele di castagno.' },
      { src: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/36lh9r0t_Fresco.png', alt: 'Fresco', caption: 'Mega Panino Artigianale di Semola caldo con una marea di Mortadella, Rucola e la nostra Burrata Pugliese Fresca e una manciata di Dolci Pomodorini Semi Secchi Homemade.' },
    ],
    bullets_before_images: true,
    bullets: [
      {
        title: 'Il Pane',
        bad: 'La classica Rosetta fatta con farina di basso livello che si sfalda in bocca al primo morso.',
        good: 'Un filone di semola di grano duro rimacinata Bio, preparato artigianalmente da un fornaio locale — croccante fuori, morbido dentro.',
      },
      {
        title: 'La Mortadella',
        bad: 'La classica Mortadella che ti propinano nei posti turistici e che non sa di niente.',
        good: 'Mortadella IGP di alta qualità, una delle più goduriose che ci siano.',
      },
      {
        title: 'Gli Ingredienti',
        bad: 'Niente compromessi, niente scorciatoie, niente "fast food per turisti".',
        good: 'Da Sfarinà trovi solo ingredienti di prima scelta.',
      },
    ],
    offer_kicker: 'Bibita in omaggio',
    offer_h: '1 bibita in Omaggio sul primo ordine',
    offer_body: 'Lascia i tuoi dati e ricevi il coupon su WhatsApp — ordini un panino da Sfarinà e ti regaliamo una bibita fresca.',
    cta_main: 'Sblocca la tua Bibita in Omaggio',
    cta_secondary: 'Vai al menù',
    menu_pdf: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/q9te07ql_Menu%20-%20Mortadella%20Factory%2031%2705%2726%20-%20A0%20scala%20149.pdf',
    cta_type: 'lead',
    lead_modal_copy: {
      kicker: 'Mortadella Factory',
      heading: 'Ricevi la tua Bibita in omaggio',
      subheading: 'Lascia i tuoi dati, ti arriva il coupon su WhatsApp. Lo usi sul tuo primo Mega Panino con la Mortadella IGP da Sfarinà.',
      submit: 'Voglio la mia bibita gratis',
      success_body: 'A breve ti arriva il coupon su WhatsApp. Ti aspettiamo nel cuore di Bologna con il tuo Mega Panino e una bibita offerta da noi 🍻',
      offer_tag: 'Mortadella — 1 bibita in omaggio sul primo panino',
    },
    i18n_en: {
      seo_title: 'Sfarinà — Mortadella IGP Sandwich in Bologna',
      hero_kicker: 'Tourist',
      hero_h1: "Don't get ripped off by tourist traps",
      hero_h2: 'Taste the most flavorful Mortadella sandwich in Bologna.',
      trust: 'Organic semolina bread • Mortadella IGP sliced fresh • Top-quality ingredients',
      showcase_title: 'Why is our Sandwich better?',
      showcase_subtitle: 'Three sharp differences you can taste at the first bite.',
      bullets: [
        { title: 'The Bread', bad: 'The classic Rosetta made with low-quality flour that crumbles at the first bite.', good: 'A loaf of organic durum wheat semolina, handcrafted by a local baker — crispy outside, soft inside.' },
        { title: 'The Mortadella', bad: 'The usual flavorless Mortadella served in tourist spots.', good: 'High-quality IGP Mortadella, one of the most delicious you can find.' },
        { title: 'The Ingredients', bad: 'No compromises, no shortcuts, no "fast food for tourists".', good: 'At Sfarinà you\'ll find only top-quality ingredients.' },
      ],
      offer_kicker: 'Free drink',
      offer_h: '1 free drink on your first order',
      offer_body: 'Leave your details and receive the coupon on WhatsApp — order a sandwich from Sfarinà and we\'ll give you a fresh drink.',
      cta_main: 'Get your free drink',
      cta_secondary: 'See the menu',
      lead_modal_copy: {
        kicker: 'Mortadella Factory',
        heading: 'Get your free drink',
        subheading: 'Leave your details, your coupon arrives on WhatsApp. Use it on your first Mega Sandwich with IGP Mortadella at Sfarinà.',
        submit: 'I want my free drink',
        success_body: 'Your coupon will arrive on WhatsApp shortly. We\'re waiting for you in the heart of Bologna with your Mega Sandwich and a drink on us 🍻',
      },
    },
  },
};

// Mapping slug URL → variante config (alias multipli supportati)
const SLUG_MAP = {
  budget: 'budget', 'rotolo-60cm': 'budget', rotolo: 'budget',
  vegan: 'vegan', vegano: 'vegan',
  group: 'group', degustazione: 'group', gruppo: 'group',
  premium: 'premium', 'mortadella-igp': 'premium', mortadella: 'premium', 'anti-turista': 'premium',
};

export default function LandingVariant() {
  const { slug } = useParams();
  const variantKey = SLUG_MAP[slug] || 'budget';
  const baseVariant = VARIANTS[variantKey];
  const { lang } = useT();

  // Merge EN translations on top of the IT defaults when lang === 'en'.
  // i18n_en contains only the keys that need to be overridden (DRY-friendly).
  const v = lang === 'en' && baseVariant.i18n_en
    ? {
        ...baseVariant,
        ...baseVariant.i18n_en,
        lead_modal_copy: baseVariant.lead_modal_copy && baseVariant.i18n_en.lead_modal_copy
          ? { ...baseVariant.lead_modal_copy, ...baseVariant.i18n_en.lead_modal_copy }
          : (baseVariant.i18n_en.lead_modal_copy || baseVariant.lead_modal_copy),
      }
    : baseVariant;

  const [leadOpen, setLeadOpen] = useState(false);
  const [reservOpen, setReservOpen] = useState(false);
  // Conta le aperture del ReservationModal per resettarlo via `key` (re-mount on each open).
  const [reservSession, setReservSession] = useState(0);

  // Tracking PageView all'apertura (server-side + client-side, dedup via event_id)
  useEffect(() => {
    if (typeof document !== 'undefined') document.title = v.seo_title;
    trackEventDedup('PageView', { context: 'lp_load', source: `lp-${v.theme}`, lang });
  }, [v, lang]);

  const openCta = useCallback((src) => {
    sendServerSideEvent('ButtonClick', { button_label: v.cta_main, source: `lp-${v.theme}-${src}` });
    if (v.cta_type === 'reservation') {
      setReservSession((n) => n + 1);
      setReservOpen(true);
    } else {
      setLeadOpen(true);
    }
  }, [v, sendServerSideEvent]);

  const trackClick = (label, extra = {}) => () => {
    sendServerSideEvent('ButtonClick', { button_label: label, source: `lp-${v.theme}`, ...extra });
  };

  return (
    <div data-theme={v.theme} className="lp-root font-['Lora',_serif] min-h-screen scroll-smooth" lang={lang}>
      {/* CSS variables per i 4 temi cromatici (vedi Tailwind config) */}
      <style>{`
        .lp-root { background: var(--lp-bg); color: var(--lp-fg); }
        .lp-root[data-theme="budget"] {
          --lp-bg: #FFF8EE; --lp-fg: #1A1A1A; --lp-muted: #5a4a42;
          --lp-primary: #E84A1D; --lp-primary-hover: #C03911;
          --lp-accent: #F5B100; --lp-card: #FFFFFF; --lp-border: #F0D9A7;
          --color-accent: #E84A1D;
        }
        .lp-root[data-theme="vegan"] {
          --lp-bg: #F4F1E8; --lp-fg: #1F2A22; --lp-muted: #5a6b5d;
          --lp-primary: #3C5F3F; --lp-primary-hover: #2A4A2C;
          --lp-accent: #8FA688; --lp-card: #FFFFFF; --lp-border: #D9D2BD;
          --color-accent: #3C5F3F;
        }
        .lp-root[data-theme="group"] {
          --lp-bg: #FBF1E2; --lp-fg: #2A1810; --lp-muted: #6b5039;
          --lp-primary: #B5471E; --lp-primary-hover: #8E3414;
          --lp-accent: #D8A23B; --lp-card: #FFFFFF; --lp-border: #E8D2A3;
          --color-accent: #B5471E;
        }
        .lp-root[data-theme="premium"] {
          --lp-bg: #161616; --lp-fg: #F2EBD8; --lp-muted: #C9B98E;
          --lp-primary: #C9A24B; --lp-primary-hover: #B08C36;
          --lp-accent: #6B0F1A; --lp-card: #1F1F1F; --lp-border: #2F2F2F;
          --color-accent: #C9A24B;
        }

        @keyframes lp-pulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 var(--lp-primary); }
          70% { transform: scale(1.02); box-shadow: 0 0 0 16px transparent; }
          100% { transform: scale(1); box-shadow: 0 0 0 0 transparent; }
        }
        .lp-cta-pulse { animation: lp-pulse 2.2s infinite; }
      `}</style>

      {/* HEADER minimal */}
      <header className="border-b" style={{ borderColor: 'var(--lp-border)' }}>
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between gap-3">
          <Link to="/" data-testid="lp-logo"
            className="font-['Poppins',_sans-serif] font-black text-2xl md:text-3xl tracking-tighter"
            style={{ color: 'var(--lp-primary)' }}>
            SFARINÀ.
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button type="button" onClick={() => openCta('header')} data-testid="lp-header-cta"
              className="hidden sm:inline-flex font-['Poppins',_sans-serif] font-bold text-sm py-2 px-5 rounded-full text-white transition-colors"
              style={{ background: 'var(--lp-primary)' }}>
              {v.cta_main}
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="px-4 py-12 md:py-20" style={{ background: variantKey === 'premium' ? '#0E0E0E' : 'var(--lp-bg)' }}>
        {v.hero_image ? (
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
            <div className="text-center md:text-left order-2 md:order-1">
              <p className="font-['Poppins',_sans-serif] uppercase text-xs tracking-[0.3em] mb-4" style={{ color: 'var(--lp-primary)' }} data-testid="lp-hero-kicker">{v.hero_kicker}</p>
              <h1 className="font-['Poppins',_sans-serif] font-black text-3xl md:text-5xl lg:text-6xl !leading-[1.05] mb-6" data-testid="lp-hero-h1">
                {v.hero_h1}
              </h1>
              <h2 className="font-['Poppins',_sans-serif] font-medium text-lg md:text-xl !leading-snug mb-8" style={{ color: 'var(--lp-muted)' }} data-testid="lp-hero-h2">
                {v.hero_h2}
              </h2>
              <button type="button" onClick={() => openCta('hero')} data-testid="lp-hero-cta"
                className="lp-cta-pulse inline-flex items-center justify-center font-['Poppins',_sans-serif] font-bold text-base md:text-lg py-4 px-8 rounded-full text-white shadow-xl transition-transform"
                style={{ background: 'var(--lp-primary)' }}>
                {v.cta_main} →
              </button>
            </div>
            <div className="order-1 md:order-2">
              <div className="aspect-square w-full rounded-2xl overflow-hidden shadow-2xl">
                <img src={v.hero_image.src} alt={v.hero_image.alt} className="w-full h-full object-cover" loading="eager" />
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-['Poppins',_sans-serif] uppercase text-xs tracking-[0.3em] mb-4" style={{ color: 'var(--lp-primary)' }} data-testid="lp-hero-kicker">{v.hero_kicker}</p>
            <h1 className="font-['Poppins',_sans-serif] font-black text-3xl md:text-5xl lg:text-6xl !leading-[1.1] mb-6" data-testid="lp-hero-h1">
              {v.hero_h1}
            </h1>
            <h2 className="font-['Poppins',_sans-serif] font-medium text-lg md:text-xl !leading-snug max-w-3xl mx-auto mb-8" style={{ color: 'var(--lp-muted)' }} data-testid="lp-hero-h2">
              {v.hero_h2}
            </h2>
            <button type="button" onClick={() => openCta('hero')} data-testid="lp-hero-cta"
              className="lp-cta-pulse inline-flex items-center justify-center font-['Poppins',_sans-serif] font-bold text-base md:text-lg py-4 px-8 rounded-full text-white shadow-xl transition-transform"
              style={{ background: 'var(--lp-primary)' }}>
              {v.cta_main} →
            </button>
          </div>
        )}
      </section>

      {/* TRUST BAR — modalità "positiva" (default) o "pain point" se trust_is_pain */}
      {v.trust_is_pain ? (
        <section className="py-7 border-y bg-red-50" style={{ borderColor: '#fecaca' }} data-testid="lp-trust">
          <div className="max-w-3xl mx-auto px-4">
            <div className="flex items-start gap-3">
              <span className="inline-flex shrink-0 items-center justify-center w-9 h-9 rounded-full bg-red-100 border border-red-300 text-red-600" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </span>
              <div>
                <p className="font-['Poppins',_sans-serif] uppercase text-[10px] tracking-[0.25em] text-red-700 font-bold mb-1">{lang === 'en' ? 'The pain point' : 'Il problema'}</p>
                <p className="font-['Poppins',_sans-serif] font-medium text-sm md:text-base text-red-900 !leading-snug">{v.trust}</p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-5 border-y" style={{ background: 'var(--lp-accent)', borderColor: 'var(--lp-border)' }} data-testid="lp-trust">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <p className="font-['Poppins',_sans-serif] font-bold text-sm md:text-base text-white tracking-wide flex items-center justify-center gap-2">
              <Sparkles size={18} className="shrink-0" />
              {v.trust}
            </p>
          </div>
        </section>
      )}

      {/* PRODUCT SHOWCASE */}
      <section className="py-16 md:py-20 px-4" data-testid="lp-showcase">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h3 className="font-['Poppins',_sans-serif] font-bold text-3xl md:text-4xl !leading-tight mb-3">{v.showcase_title}</h3>
            <p className="text-base md:text-lg" style={{ color: 'var(--lp-muted)' }}>{v.showcase_subtitle}</p>
          </div>

          {/* Helper: blocco IMMAGINI (3-5 foto prodotti) */}
          {(() => {
            const imagesBlock = (
              <div key="images" className={`grid gap-5 md:gap-6 ${v.showcase_images.length >= 5 ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'}`}>
                {v.showcase_images.map((img) => (
                  <figure key={img.alt} className="relative overflow-hidden rounded-2xl shadow-lg group" data-testid={`lp-showcase-${img.alt}`}>
                    <img src={img.src} alt={img.alt} className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    <figcaption className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-black/90 via-black/65 to-transparent text-white">
                      <p className="font-['Poppins',_sans-serif] font-bold text-sm md:text-base !leading-tight">{img.alt}</p>
                      <p className="text-xs md:text-[13px] opacity-95 !leading-snug mt-1">{img.caption}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
            );
            // Helper: blocco BULLETS "Non è / È" — solo se v.bullets è valorizzato
            const bulletsBlock = v.bullets ? (
              <ul key="bullets" className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto" data-testid="lp-showcase-bullets">
                {v.bullets.map((b) => (
                  <li key={b.title} className="p-6 rounded-xl border flex flex-col gap-4" style={{ background: 'var(--lp-card)', borderColor: 'var(--lp-border)' }}>
                    <h4 className="font-['Poppins',_sans-serif] font-bold text-xl" style={{ color: 'var(--lp-primary)' }}>{b.title}</h4>
                    {b.bad && b.good ? (
                      <>
                        <div className="relative rounded-lg border border-red-500/40 bg-red-500/5 p-4 overflow-hidden" data-testid={`bullet-${b.title}-bad`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 border border-red-500/50 text-red-400" aria-hidden="true">
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>
                            </span>
                            <span className="font-['Poppins',_sans-serif] font-black text-[10px] uppercase tracking-[0.2em] text-red-400">{lang === 'en' ? "It's NOT" : 'Non è'}</span>
                          </div>
                          <p className="text-sm md:text-[15px] !leading-relaxed line-through decoration-red-500/45 decoration-[1.5px]" style={{ color: 'var(--lp-muted)' }}>{b.bad}</p>
                        </div>
                        <div className="relative rounded-lg p-4 overflow-hidden" style={{ background: 'var(--lp-primary)', color: '#fff' }} data-testid={`bullet-${b.title}-good`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 border border-white/40 text-white" aria-hidden="true">
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            </span>
                            <span className="font-['Poppins',_sans-serif] font-black text-[10px] uppercase tracking-[0.2em] opacity-90">{lang === 'en' ? "It's" : 'È'}</span>
                          </div>
                          <p className="text-sm md:text-[15px] font-medium !leading-relaxed">{b.good}</p>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm md:text-base !leading-relaxed" style={{ color: 'var(--lp-muted)' }}>{b.body}</p>
                    )}
                  </li>
                ))}
              </ul>
            ) : null;

            const longListBlock = v.showcase_long ? (
              <ul key="long" className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-testid="lp-showcase-long">
                {v.showcase_long.map((d) => (
                  <li key={d.name} className="p-5 md:p-6 rounded-xl border" style={{ background: 'var(--lp-card)', borderColor: 'var(--lp-border)' }}>
                    <h4 className="font-['Poppins',_sans-serif] font-bold text-lg mb-2" style={{ color: 'var(--lp-primary)' }}>{d.name}</h4>
                    <p className="text-sm md:text-base !leading-relaxed" style={{ color: 'var(--lp-muted)' }}>{d.desc}</p>
                  </li>
                ))}
              </ul>
            ) : null;

            // Quando v.bullets_before_images === true (caso Premium), inverto l'ordine:
            // 1) bullets "Il Pane / La Mortadella / Gli Ingredienti" con "Non è / È"
            // 2) divider elegante
            // 3) 3 foto panini
            if (v.bullets_before_images && bulletsBlock) {
              return (
                <div className="flex flex-col gap-12 md:gap-16">
                  {bulletsBlock}
                  <div className="flex items-center justify-center gap-3 max-w-md mx-auto w-full" aria-hidden="true">
                    <span className="h-px flex-1" style={{ background: 'var(--lp-border)' }}></span>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--lp-primary)', opacity: 0.5 }}></span>
                    <span className="h-px flex-1" style={{ background: 'var(--lp-border)' }}></span>
                  </div>
                  {imagesBlock}
                  {longListBlock}
                </div>
              );
            }
            // Default: prima immagini, poi long-list (group), poi bullets
            return (
              <div className="flex flex-col gap-12 md:gap-16">
                {imagesBlock}
                {longListBlock}
                {bulletsBlock}
              </div>
            );
          })()}
        </div>
      </section>

      {/* OFFER + CTA finale (colore d'accento, pulsante pulsante) */}
      <section className="py-16 md:py-20 px-4" style={{ background: 'var(--lp-primary)' }} data-testid="lp-offer">
        <div className="max-w-3xl mx-auto text-center text-white">
          <p className="font-['Poppins',_sans-serif] uppercase text-xs tracking-[0.3em] opacity-90 mb-4">{v.offer_kicker}</p>
          <h3 className="font-['Poppins',_sans-serif] font-black text-3xl md:text-5xl !leading-tight mb-5">{v.offer_h}</h3>
          <p className="text-base md:text-lg opacity-95 !leading-relaxed mb-9 max-w-2xl mx-auto">{v.offer_body}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button type="button" onClick={() => openCta('offer')} data-testid="lp-offer-cta"
              className="lp-cta-pulse inline-flex items-center justify-center font-['Poppins',_sans-serif] font-black text-base md:text-lg py-4 px-10 rounded-full bg-white shadow-2xl transition-transform hover:scale-105"
              style={{ color: 'var(--lp-primary)' }}>
              {v.cta_main} →
            </button>
            {v.cta_secondary && (
              v.menu_pdf ? (
                <a href={v.menu_pdf} target="_blank" rel="noopener noreferrer"
                  onClick={trackClick('lp-secondary-cta-pdf')} data-testid="lp-secondary-cta"
                  className="text-white border-b-2 border-white/40 hover:border-white font-['Poppins',_sans-serif] font-semibold pb-1 transition-colors">
                  {v.cta_secondary}
                </a>
              ) : (
                <Link to="/#menu" onClick={trackClick('lp-secondary-cta')} data-testid="lp-secondary-cta"
                  className="text-white border-b-2 border-white/40 hover:border-white font-['Poppins',_sans-serif] font-semibold pb-1 transition-colors">
                  {v.cta_secondary}
                </Link>
              )
            )}
          </div>
        </div>
      </section>

      {/* FOOTER unificato con la home (i link interni puntano a /#menu, /#sedi) */}
      <SiteFooter basePath="/" />

      {/* Modals */}
      {/* Modal LEAD personalizzato per LP (tema + copy ereditati dalla variante).
          Fallback al LeadFormModal generico se per qualche motivo manca lead_modal_copy. */}
      {v.lead_modal_copy ? (
        <LpLeadModal open={leadOpen} onClose={() => setLeadOpen(false)} source={`lp-${v.theme}`} copy={v.lead_modal_copy} />
      ) : (
        <LeadFormModal open={leadOpen} onClose={() => setLeadOpen(false)} source={`lp-${v.theme}`} />
      )}
      <ReservationModal key={`reserv-${reservSession}`} open={reservOpen} onClose={() => setReservOpen(false)} source={`lp-${v.theme}-degustazione`} />
    </div>
  );
}
